import { useCallback, useEffect, useMemo, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { getSupabase } from '../../supabaseClient';
import { readStoredAuthUserId, resolveAuthSession } from '../auth/storedSession';
import { activeUserIdRef } from './activeUserIdRef';
import { initCapacitorQueryLifecycle } from './capacitorLifecycle';
import { removeLegacyPersistedQueryCaches } from './clearQueryCache';
import { createAppQueryClient } from './queryClient';
import { QUERY_CACHE_BUSTER } from './cacheVersion';
import { createQueryPersister, shouldDehydrateMutation, shouldDehydrateQuery } from './persister';
import { registerQueryClientPersist } from './persistNow';
import { queryKeys } from './queryKeys';
import { registerMutationDefaults } from '../mutations/registerMutationDefaults';
import { initOfflineMutationPersist } from '../mutations/offlineMutationPersist';
import {
  initOfflineMutationResume,
  resumeOfflineMutationsNow,
} from '../mutations/resumeOfflineMutations';
import { importPersistedMaps } from '../festivalOffline';

const PERSIST_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Never block the UI waiting for auth. After this, boot on the stored session's user (see lib/auth/storedSession):
 * with no service getSession() is stuck refreshing an expired token, and falling back to anon here restored the anon
 * cache - Sets and Maps came up empty and the app looked signed out (operator, 2026-09-25).
 */
const AUTH_SESSION_BOOT_TIMEOUT_MS = 2500;

export default function QueryProvider({ children }) {
  const [queryClient] = useState(() => createAppQueryClient());
  const [authUserId, setAuthUserId] = useState(undefined);
  const persister = useMemo(
    () => (authUserId === undefined ? null : createQueryPersister(authUserId)),
    [authUserId],
  );

  useEffect(() => {
    let cancelled = false;
    let resolved = false;

    const finishBoot = (userId) => {
      if (cancelled || resolved) return;
      resolved = true;
      activeUserIdRef.current = userId;
      setAuthUserId(userId);
    };

    // A stored session means signed in: boot on that user's cache now rather than waiting on a refresh that may never
    // come back. onAuthStateChange below still moves us to anon on a real SIGNED_OUT (revoked token).
    const storedUserIdAtBoot = readStoredAuthUserId();
    if (storedUserIdAtBoot) finishBoot(storedUserIdAtBoot);

    const bootTimeout = setTimeout(() => {
      if (resolved) return;
      const storedUserId = readStoredAuthUserId();
      console.warn(
        `[query] auth session boot timed out; continuing with ${storedUserId ? 'stored user' : 'anon'} cache`,
      );
      finishBoot(storedUserId);
    }, AUTH_SESSION_BOOT_TIMEOUT_MS);

    removeLegacyPersistedQueryCaches();

    getSupabase()
      .auth.getSession()
      .then(({ data: { session } }) => {
        clearTimeout(bootTimeout);
        finishBoot(resolveAuthSession(session)?.user?.id ?? null);
      })
      .catch((err) => {
        clearTimeout(bootTimeout);
        console.warn('[query] getSession failed; continuing with stored session if any:', err);
        finishBoot(readStoredAuthUserId());
      });

    const {
      data: { subscription },
    } = getSupabase().auth.onAuthStateChange((event, session) => {
      // A null INITIAL_SESSION / failed refresh offline is not a sign-out: keep the stored user's cache.
      const userId = resolveAuthSession(session, { event })?.user?.id ?? null;
      activeUserIdRef.current = userId;
      setAuthUserId(userId);
    });

    return () => {
      cancelled = true;
      clearTimeout(bootTimeout);
      subscription.unsubscribe();
    };
  }, []);

  // A browsed lineup (an event you are not going to) is not saved: the going set is read from the rsvps cache.
  const dehydrateQuery = useCallback(
    (query) => {
      const rsvps = authUserId ? queryClient.getQueryData(queryKeys.rsvps.mine(authUserId)) : null;
      const going = Array.isArray(rsvps)
        ? new Set(rsvps.filter((r) => r.status === 'going').map((r) => String(r.event_id)))
        : null;
      return shouldDehydrateQuery(query, going);
    },
    [queryClient, authUserId],
  );

  useEffect(() => {
    if (authUserId === undefined || !persister) return undefined;
    return registerQueryClientPersist({
      queryClient,
      persister,
      buster: QUERY_CACHE_BUSTER,
      dehydrateOptions: {
        shouldDehydrateQuery: dehydrateQuery,
        shouldDehydrateMutation,
      },
    });
  }, [queryClient, persister, authUserId, dehydrateQuery]);

  useEffect(() => {
    if (authUserId === undefined) return undefined;
    registerMutationDefaults(queryClient);
    const stopPersist = initOfflineMutationPersist(queryClient);
    const stopResume = initOfflineMutationResume(queryClient);
    const stopLifecycle = initCapacitorQueryLifecycle(queryClient, () => activeUserIdRef.current, {
      onResumeMutations: () => resumeOfflineMutationsNow(queryClient, 'lifecycle-resume'),
    });
    return () => {
      stopPersist();
      stopResume();
      stopLifecycle();
    };
  }, [queryClient, authUserId]);

  if (authUserId === undefined || !persister) {
    return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
  }

  return (
    <PersistQueryClientProvider
      key={authUserId ?? 'anon'}
      client={queryClient}
      persistOptions={{
        persister,
        buster: QUERY_CACHE_BUSTER,
        // A week, not the 24 h default: a map fetched 36 h before the gates must still restore on a cold start
        // at the festival with no service (operator, 2026-09-23).
        maxAge: PERSIST_MAX_AGE_MS,
        dehydrateOptions: {
          shouldDehydrateQuery: dehydrateQuery,
          shouldDehydrateMutation,
        },
      }}
      onSuccess={() => {
        registerMutationDefaults(queryClient);
        resumeOfflineMutationsNow(queryClient, 'persist-restore');
        void importPersistedMaps(queryClient);
      }}
    >
      {children}
    </PersistQueryClientProvider>
  );
}
